import { alpha, createTheme, Theme } from '@mui/material/styles'
import type { AppearanceConfig } from '../components/MainMenu/sections/admin/EnvironmentConfig/types/EnvironmentConfig.types'
import type { SevvThemeTokens } from './sevvThemeTokenModel'
import { resolveEffectiveChrome } from './sevvThemeTokenModel'
import { resolveThemeMode } from './resolveWorkspaceTheme'

function fontWeightFromAppearance(w: AppearanceConfig['typography']['fontWeight']): number {
  if (w === 'light') return 300
  if (w === 'bold') return 700
  return 400
}

function fontSizeRem(base: number, appearance: AppearanceConfig): string {
  const mult =
    appearance.typography.fontSize === 'small' ? 0.875 : appearance.typography.fontSize === 'large' ? 1.125 : 1
  return `${(base * mult).toFixed(3)}rem`
}

/**
 * Builds the active MUI theme from workspace tokens + appearance (same inputs as CSS variables).
 */
export function buildSevvMuiTheme(
  tokens: SevvThemeTokens,
  appearance: AppearanceConfig,
  prefersDark: boolean
): Theme {
  const mode = resolveThemeMode(appearance, prefersDark)
  const p =
    mode === 'dark' && tokens.darkPalette
      ? { ...tokens.palette, ...tokens.darkPalette }
      : tokens.palette

  const primaryMain = p.primary
  const chrome = resolveEffectiveChrome(tokens, p, mode)
  const fontFamily = appearance.typography.fontFamily
  const fw = fontWeightFromAppearance(appearance.typography.fontWeight)
  const transitionMs = appearance.animations.enabled ? Math.max(0, appearance.animations.duration) : 0
  const easing = appearance.animations.enabled ? appearance.animations.easing : 'linear'

  const rgbShadow = (opacity: number) => {
    const h = primaryMain.replace('#', '')
    const full =
      h.length === 3
        ? [0, 1, 2].map((i) => parseInt(h[i] + h[i], 16))
        : [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)]
    return `0 4px 14px 0 rgba(${full[0]}, ${full[1]}, ${full[2]}, ${opacity})`
  }

  return createTheme({
    palette: {
      mode,
      primary: {
        main: primaryMain,
        light: p.primaryLight,
        dark: p.primaryDark,
        contrastText: '#ffffff',
      },
      secondary: {
        main: p.secondary,
        contrastText: '#ffffff',
      },
      success: { main: p.success, contrastText: '#ffffff' },
      warning: { main: p.warning, contrastText: '#ffffff' },
      error: { main: p.error, contrastText: '#ffffff' },
      info: { main: p.info, contrastText: '#ffffff' },
      background: {
        default: p.backgroundDefault,
        paper: p.backgroundPaper,
      },
      text: {
        primary: p.textPrimary,
        secondary: p.textSecondary,
        disabled: mode === 'dark' ? '#666666' : '#bdbdbd',
      },
      divider: p.divider,
    },
    typography: {
      fontFamily,
      fontWeightLight: 300,
      fontWeightRegular: 400,
      fontWeightMedium: 500,
      fontWeightBold: 700,
      h1: { fontWeight: Math.max(fw, 600), color: p.textPrimary, fontSize: fontSizeRem(2.25, appearance) },
      h2: { fontWeight: Math.max(fw, 600), color: p.textPrimary, fontSize: fontSizeRem(1.75, appearance) },
      h3: { fontWeight: Math.max(fw, 600), color: p.textPrimary, fontSize: fontSizeRem(1.5, appearance) },
      h4: { fontWeight: Math.max(fw, 600), color: p.textPrimary, fontSize: fontSizeRem(1.25, appearance) },
      h5: { fontWeight: Math.max(fw, 600), color: p.textPrimary, fontSize: fontSizeRem(1.125, appearance) },
      h6: { fontWeight: Math.max(fw, 600), color: p.textPrimary, fontSize: fontSizeRem(1, appearance) },
      body1: { color: p.textPrimary, fontSize: fontSizeRem(1, appearance), fontWeight: fw },
      body2: { color: p.textSecondary, fontSize: fontSizeRem(0.875, appearance), fontWeight: fw },
    },
    shape: { borderRadius: 8 },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            scrollbarColor: mode === 'dark' ? '#555 #1a1a1a' : '#bdbdbd #f5f5f5',
            '&::-webkit-scrollbar, & *::-webkit-scrollbar': {
              backgroundColor: mode === 'dark' ? '#1a1a1a' : '#f5f5f5',
            },
            '&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb': {
              borderRadius: '8px',
              backgroundColor: mode === 'dark' ? '#555' : '#bdbdbd',
              minHeight: 24,
              border: mode === 'dark' ? '3px solid #1a1a1a' : '3px solid #f5f5f5',
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '8px',
            textTransform: 'none',
            fontWeight: 500,
            boxShadow: 'none',
            ...(appearance.animations.enabled
              ? { transition: `all ${transitionMs}ms ${easing}` }
              : { transition: 'none' }),
            '&:hover': {
              boxShadow: rgbShadow(0.3),
            },
          },
          contained: {
            backgroundColor: primaryMain,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: p.primaryDark,
            },
          },
          outlined: {
            borderColor: primaryMain,
            color: primaryMain,
            '&:hover': {
              backgroundColor: p.primaryPale,
              borderColor: p.primaryDark,
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            backgroundColor: p.backgroundPaper,
            border: `1px solid ${p.divider}`,
            borderRadius: '12px',
            boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            backgroundColor: p.backgroundPaper,
            color: p.textPrimary,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            alignItems: 'flex-start',
          },
          standardSuccess: ({ theme: t }) => {
            const m = t.palette.success.main
            const a = t.palette.mode === 'dark' ? 0.2 : 0.12
            return {
              backgroundColor: alpha(m, a),
              color: t.palette.text.primary,
              '& .MuiAlert-icon': { color: m },
            }
          },
          standardWarning: ({ theme: t }) => {
            const m = t.palette.warning.main
            const a = t.palette.mode === 'dark' ? 0.22 : 0.12
            return {
              backgroundColor: alpha(m, a),
              color: t.palette.text.primary,
              '& .MuiAlert-icon': { color: m },
            }
          },
          standardError: ({ theme: t }) => {
            const m = t.palette.error.main
            const a = t.palette.mode === 'dark' ? 0.22 : 0.12
            return {
              backgroundColor: alpha(m, a),
              color: t.palette.text.primary,
              '& .MuiAlert-icon': { color: m },
            }
          },
          standardInfo: ({ theme: t }) => {
            const m = t.palette.info.main
            const a = t.palette.mode === 'dark' ? 0.2 : 0.12
            return {
              backgroundColor: alpha(m, a),
              color: t.palette.text.primary,
              '& .MuiAlert-icon': { color: m },
            }
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: { borderRadius: '12px', fontWeight: 500 },
          colorPrimary: {
            backgroundColor: primaryMain,
            color: '#ffffff',
          },
          colorSecondary: {
            backgroundColor: p.secondary,
            color: '#ffffff',
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: 8,
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            backgroundColor: `var(--sevv-bg-paper, ${p.backgroundPaper})`,
            color: `var(--sevv-text-primary, ${p.textPrimary})`,
            border: `1px solid var(--sevv-divider, ${p.divider})`,
          },
        },
      },
      MuiDialogTitle: {
        styleOverrides: {
          root: {
            background: chrome.dialogTitleBackground,
            color: chrome.dialogTitleColor,
            borderBottom: chrome.dialogTitleBorderBottom,
            fontWeight: 600,
            /** Match virtual-room / shell modals: optical balance above the divider. */
            padding: '18px 24px 16px',
          },
        },
      },
      MuiDialogContent: {
        styleOverrides: {
          root: {
            padding: '24px',
            /**
             * MUI zeros padding-top when content follows DialogTitle, which reads cramped.
             * Restore a deliberate inset under the title chrome.
             */
            '.MuiDialogTitle-root + &': {
              paddingTop: '24px',
            },
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            backgroundColor: chrome.dialogActionsBackground,
            borderTop: chrome.dialogActionsBorderTop,
          },
        },
      },
      MuiModal: {
        styleOverrides: {
          backdrop: {
            backgroundColor: chrome.overlayBackdrop,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: chrome.shellSidebarBackground,
            borderColor: p.divider,
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: chrome.shellHeaderBackground,
            color: '#ffffff',
          },
          colorPrimary: {
            backgroundColor: chrome.shellHeaderBackground,
            color: '#ffffff',
          },
        },
      },
      MuiMenu: {
        styleOverrides: {
          paper: { borderRadius: 8, backgroundColor: p.backgroundPaper },
        },
      },
    },
  })
}
